import axios from "axios";
import { getAccessToken, getCurrentLanguage } from "@shared/utils/helpers";

export const SESSION_EXPIRED_FLAG = "sessionExpired";
window.__SOPHY_SESSION_EXPIRY_IN_PROGRESS__ =
  window.__SOPHY_SESSION_EXPIRY_IN_PROGRESS__ || false;

// ---------------------------------------------------------------------------
// URL contract (D3 API consolidation)
// ---------------------------------------------------------------------------
// Call sites pass a PATH, never a full URL: `baseURL` below already supplies
// `process.env.REACT_APP_SOPHYAPP_API_URL`. Two placeholder segments in that
// path are expanded by the request interceptor further down, at request
// time, from the same sources every hand-rolled URL used to read directly:
//
//   :locale  -> getCurrentLanguage()   e.g. "de", "en"
//   :token   -> getAccessToken()       the patient/therapist access token
//
// Example:
//   http.get(`/:locale/react/:token/notifications`)
//   -> GET {REACT_APP_SOPHYAPP_API_URL}/de/react/<token>/notifications
//
// Rules for call sites:
//   - Use `:locale` / `:token` ONLY where the original hand-built URL called
//     getCurrentLanguage()/getAccessToken() dynamically. Where the original
//     hard-coded a literal instead (e.g. "/de/v2/...", or the "/0000/"
//     placeholder segment some account-creation endpoints use before a real
//     token exists), keep writing that literal — do not "fix" it into
//     :locale/:token, that would change today's behaviour.
//   - Public/unauthenticated endpoints simply omit `:token` from the path.
//     There is no separate "public" client: the placeholder is opt-in per
//     path, so omitting it is the explicit way to say "no token here"
//     rather than force-fitting every request through one shape.
//   - `:locale` / `:token` are plain substrings (not regex) and are replaced
//     everywhere they occur in the path, including in the query string.
// ---------------------------------------------------------------------------

const LOCALE_PLACEHOLDER = ":locale";
const TOKEN_PLACEHOLDER = ":token";

const expandUrlPlaceholders = (url) => {
  if (typeof url !== "string") {
    return url;
  }
  let expanded = url;
  if (expanded.includes(LOCALE_PLACEHOLDER)) {
    expanded = expanded.split(LOCALE_PLACEHOLDER).join(getCurrentLanguage());
  }
  if (expanded.includes(TOKEN_PLACEHOLDER)) {
    expanded = expanded.split(TOKEN_PLACEHOLDER).join(getAccessToken());
  }
  return expanded;
};

const http = axios.create({
  baseURL: process.env.REACT_APP_SOPHYAPP_API_URL,
  headers: {
    Accept: "application/json",
  },
});

// Expand :locale/:token placeholders on every request made through this
// instance. Registered unconditionally (module-load side effect, like the
// interceptors below) so it is active before any component can fire a call.
http.interceptors.request.use((config) => {
  config.url = expandUrlPlaceholders(config.url);
  return config;
});

const getErrorCode = (responseData) =>
  responseData?.error?.code || responseData?.code || null;

const shouldForceLogout = (error) => {
  const status = error?.response?.status;
  const responseData = error?.response?.data;
  const code = getErrorCode(responseData);

  if (status === 401 || status === 419 || status === 440) {
    return true;
  }

  // Legacy endpoints often return invalid auth/token as 403.
  return status === 403 && ["TOKEN_INVALID", "SESSION_EXPIRED"].includes(code);
};

export const handleSessionExpiry = () => {
  if (window.__SOPHY_SESSION_EXPIRY_IN_PROGRESS__) {
    return;
  }
  window.__SOPHY_SESSION_EXPIRY_IN_PROGRESS__ = true;
  localStorage.removeItem("access_token");
  localStorage.removeItem("userData");
  localStorage.setItem(SESSION_EXPIRED_FLAG, "1");
  // Always force a full reload to reset in-memory auth state.
  window.location.assign("/");
};

let interceptorRegistered = false;

export const setupAuthInterceptors = () => {
  if (interceptorRegistered) {
    return;
  }

  const responseErrorHandler = (error) => {
    if (shouldForceLogout(error)) {
      handleSessionExpiry();
    }
    return Promise.reject(error);
  };

  http.interceptors.response.use(
    (response) => response,
    responseErrorHandler
  );
  // Catch 401/invalid-token also for modules that still use raw axios.
  axios.interceptors.response.use((response) => response, responseErrorHandler);

  interceptorRegistered = true;
};

// Register interceptors immediately on module load so they are active before
// any component's useEffect can fire an API call (children effects run before
// the App.js parent effect, causing a race condition if we register lazily).
setupAuthInterceptors();

export default http;
